/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Returns the registrable base domain for the current host, prefixed with a
 * leading dot so cookies are shared across subdomains
 * (e.g. `account.airasia.com` -> `.airasia.com`, `airasia.com` -> `.airasia.com`).
 *
 * Returns `null` for single-label hosts such as `localhost`, so callers omit
 * the cookie `domain` attribute and let the cookie default to the current host.
 */
export const getBaseDomain = (): string | null => {
  if (typeof window === 'undefined' || !window.location?.hostname) return null;

  const parts = window.location.hostname.split('.');
  if (parts.length < 2) return null;

  return `.${parts.slice(-2).join('.')}`;
};

/**
 * Writes a browser cookie scoped to the {@link getBaseDomain} so it is
 * available across subdomains. `SameSite=Lax` is always set: every consumer is
 * an airasia.com subdomain, so same-site, and the cookie is never sent as an
 * API credential.
 *
 * The `value` is written verbatim — callers are responsible for encoding it
 * (e.g. `encodeURIComponent(JSON.stringify(data))`).
 *
 * @param name    Cookie name.
 * @param value   Pre-encoded cookie value.
 * @param exdays  Expiry in days from now.
 * @param isSecure Whether to append the `Secure` attribute. Defaults to `true`;
 *                 pass `false` for local/non-HTTPS development.
 */
export const setCookie = (
  name: string,
  value: string,
  exdays: number,
  isSecure = true,
): void => {
  if (typeof document === 'undefined') return;

  const expires = new Date(Date.now() + exdays * MILLISECONDS_PER_DAY);
  const domain = getBaseDomain();
  const domainAttr = domain ? `domain=${domain};` : '';
  const secureAttr = isSecure ? 'secure;' : '';

  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;${domainAttr}${secureAttr}`;
};

/**
 * Deletes a cookie previously written by {@link setCookie} by expiring it on the
 * same base domain and path.
 *
 * The domain/path must mirror {@link setCookie} exactly — otherwise the browser
 * treats it as a different cookie and leaves the original in place.
 *
 * @param name Cookie name to delete.
 */
export const removeCookie = (name: string): void => {
  if (typeof document === 'undefined') return;

  const domain = getBaseDomain();
  const domainAttr = domain ? `domain=${domain};` : '';

  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;${domainAttr}`;
};
